/**
 * Оболочка вкладки «Голоса клиента» (#848) — одна на все вкладки: отказ
 * сервера словами (`VoiceLocked`), фильтр потока с меню выгрузки, подсказка
 * вкладки, затем загрузка, ошибка с повтором или содержимое.
 */
import type { ReactNode } from 'react';
import { StreamFilterBar } from '../stream-filter/StreamFilterBar';
import type { useStreamWindow } from '../stream-filter/useStreamWindow';
import { VoiceLoadError } from './VoiceLoadError';
import { VoiceLoading } from './VoiceLoading';
import { VoiceLocked } from './VoiceLocked';
import { tierRefusalCode } from '@/lib/tier-refusal';
import type { VoiceSearch } from './voice-search';

/** What the shell reads of a query — the voice query of the tab. */
interface VoiceTabQuery<T> {
  data: T | undefined;
  error: unknown;
  isError: boolean;
  isPending: boolean;
  isFetching: boolean;
  isLoading: boolean;
  refetch: () => unknown;
}

interface VoiceTabShellProps<T> {
  query: VoiceTabQuery<T>;
  stream: ReturnType<typeof useStreamWindow>;
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
  hint: string;
  /** Above the hint, under the filter bar — the theme filter's strip (#2229). */
  banner?: ReactNode;
  /** The tab's export menu — on the bar while the tab loads, too (#2315). */
  exportMenu: (data: T | undefined) => ReactNode;
  upgradeHref?: string;
  children: (data: T) => ReactNode;
}

export function VoiceTabShell<T>({
  query,
  stream: { period, navigation, projectIds, callCategory },
  onSearchChange,
  hint,
  banner,
  exportMenu,
  upgradeHref,
  children,
}: VoiceTabShellProps<T>) {
  const refusal = query.isError ? tierRefusalCode(query.error) : null;
  if (refusal) return <VoiceLocked code={refusal} upgradeHref={upgradeHref} />;

  const data = query.data;
  return (
    <div className="space-y-4">
      <StreamFilterBar
        period={period}
        navigation={navigation}
        projectIds={projectIds}
        callCategory={callCategory}
        isLoading={query.isFetching && !query.isLoading}
        onSearchChange={onSearchChange}
        extraActions={exportMenu(data)}
      />

      {banner}

      <p className="text-sm text-fg-muted">{hint}</p>

      {query.isPending ? (
        <VoiceLoading />
      ) : data === undefined ? (
        // Only when there is nothing to show: a failed background refetch
        // keeps the numbers already on screen (React Query keeps `data`).
        <VoiceLoadError onRetry={() => void query.refetch()} />
      ) : (
        children(data)
      )}
    </div>
  );
}
